import { useMemo } from 'react'
import { DataTableV2GroupConfig } from '../types'

interface UseTableDataGroupingV2Props<TData> {
  data: TData[]
  groups?: TData[]
  groupConfig?: DataTableV2GroupConfig
}

export function useTableDataGroupingV2<TData>({
  data,
  groups,
  groupConfig,
}: UseTableDataGroupingV2Props<TData>) {
  return useMemo(() => {
    // If no grouping configuration or groups provided, return original data
    if (!groupConfig || !groups || groups.length === 0) {
      return data
    }

    const { groupIdCollection } = groupConfig

    // Set to track IDs that are part of a group
    const groupedItemIds = new Set<string>()

    // Process groups and map their children
    const processedGroups = groups.map((group: TData) => {
      // The property name in the JSON is specified by groupIdCollection
      const childIds = (group[groupIdCollection as keyof TData] as (string | number)[]) || []

      // Add child IDs to our set of grouped items
      childIds.forEach((id) => groupedItemIds.add(String(id)))

      // Find the actual child objects from the data array
      const subRows = data.filter((item: TData) =>
        childIds.map(String).includes(String((item as Record<string, unknown>).id))
      )

      return {
        ...group,
        id: `group-${(group as Record<string, unknown>).id}`, // Ensure unique ID for the group row
        subRows,
        isGroup: true,
      }
    })

    // Find orphan rows (items not belonging to any group)
    const orphanRows = data.filter(
      (item: TData) => !groupedItemIds.has(String((item as Record<string, unknown>).id))
    )

    // Return combined flat array of groups and orphans
    // TanStack table will handle the hierarchy via subRows
    return [...processedGroups, ...orphanRows] as TData[]
  }, [data, groups, groupConfig])
}
